import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api.js'
import { useSessao } from '../sessao.jsx'
import { pendentesDo } from '../offline.js'
import Layout from '../componentes/Layout.jsx'
import { useDados, Campo, Erro, Aviso, emDataSimples } from '../componentes/basicos.jsx'

export default function Configuracoes() {
  const { usuario, sair } = useSessao()
  const navegar = useNavigate()

  return (
    <Layout titulo="Configurações">
      <div className="colunas-desktop">
      <section className="cartao pilha">
        <span className="rotulo-secao">Conta</span>
        <div>
          <div className="titulo-item">{usuario.nome}</div>
          <div className="detalhe">{usuario.email} · {usuario.papel}</div>
        </div>
        <Link className="botao" to="/trocar-senha">Trocar senha</Link>
        <Link className="botao" to="/registros">Manutenções e despesas</Link>
        {usuario.papel === 'admin' && <Link className="botao" to="/admin">Área do administrador</Link>}
        <button type="button" className="botao-perigo"
                onClick={async () => {
                  const pendentes = await pendentesDo(usuario.id).catch(() => 0)
                  if (pendentes > 0 && !window.confirm(
                    `Há ${pendentes} lançamento(s) ainda não enviado(s). Eles ficam guardados `
                    + 'neste aparelho e vão para o servidor quando você entrar de novo. Sair mesmo assim?',
                  )) return
                  await sair()
                  navegar('/entrar', { replace: true })
                }}>
          Sair
        </button>
      </section>

      <Parametros />
      <Tokens />
      <Tema />
      </div>
    </Layout>
  )
}

/** Preços de referência usados no comparativo do dashboard (SPEC §5.7). */
function Parametros() {
  const { dados, recarregar } = useDados('/parametros')
  const [salvo, setSalvo] = useState(false)
  const [erro, setErro] = useState(null)

  const valores = dados?.parametros ?? {}
  const [gasolina, setGasolina] = useState(null)
  const [consumo, setConsumo] = useState(null)

  const valorGasolina = gasolina ?? valores.preco_gasolina ?? ''
  const valorConsumo = consumo ?? valores.consumo_referencia_km_l ?? ''

  async function salvar(evento) {
    evento.preventDefault()
    setErro(null)
    try {
      await api.put('/parametros/preco_gasolina', { valor: String(valorGasolina).replace(',', '.') })
      await api.put('/parametros/consumo_referencia_km_l', { valor: String(valorConsumo).replace(',', '.') })
      setSalvo(true)
      await recarregar()
    } catch (e) {
      setErro(e)
    }
  }

  return (
    <form className="cartao pilha" onSubmit={salvar}>
      <span className="rotulo-secao">Veículo de referência</span>
      <p className="dica">
        Serve para comparar sua garagem com um carro comum a combustão no dashboard.
      </p>

      <Campo rotulo="Preço da gasolina (R$/L)">
        <input inputMode="decimal" value={valorGasolina} onChange={(e) => setGasolina(e.target.value)} />
      </Campo>

      <Campo rotulo="Consumo de referência (km/L)">
        <input inputMode="decimal" value={valorConsumo} onChange={(e) => setConsumo(e.target.value)} />
      </Campo>

      <Erro erro={erro} />
      {salvo && <Aviso tipo="ok">Parâmetros salvos.</Aviso>}
      <button type="submit" className="botao-principal botao-largo">Salvar</button>
    </form>
  )
}

/** Tokens pessoais da camada MCP (SPEC §8). */
function Tokens() {
  const { dados, recarregar } = useDados('/auth/tokens')
  const [nome, setNome] = useState('')
  const [novo, setNovo] = useState(null)
  const [erro, setErro] = useState(null)

  async function criar(evento) {
    evento.preventDefault()
    setErro(null)
    try {
      const r = await api.post('/auth/tokens', { nome })
      setNovo(r.token)
      setNome('')
      await recarregar()
    } catch (e) {
      setErro(e)
    }
  }

  return (
    <section className="cartao pilha">
      <span className="rotulo-secao">Tokens de acesso</span>
      <p className="dica">Usados pelo lançamento por voz. Cada token vale por esta conta.</p>

      {(dados?.tokens ?? []).map((t) => (
        <div key={t.id} className="entre">
          <div>
            <div className="titulo-item">{t.nome}</div>
            <div className="detalhe">
              criado em {emDataSimples(t.criado_em?.slice(0, 10))}
              {t.revogado_em && ' · revogado'}
            </div>
          </div>
          {!t.revogado_em && (
            <button type="button" className="botao-perigo"
                    onClick={async () => { await api.remover(`/auth/tokens/${t.id}`); recarregar() }}>
              Revogar
            </button>
          )}
        </div>
      ))}

      {novo && (
        <Aviso tipo="atencao">
          Copie agora — este valor não aparece de novo:
          <br />
          <code style={{ wordBreak: 'break-all' }}>{novo}</code>
        </Aviso>
      )}

      <form className="linha" onSubmit={criar}>
        <div className="crescer">
          <input value={nome} placeholder="Nome do token (ex.: Celular)" required
                 onChange={(e) => setNome(e.target.value)} />
        </div>
        <button type="submit">Gerar</button>
      </form>

      <Erro erro={erro} />
    </section>
  )
}

function Tema() {
  const [tema, setTema] = useState(document.documentElement.dataset.tema ?? 'sistema')

  function aplicar(valor) {
    setTema(valor)
    if (valor === 'sistema') delete document.documentElement.dataset.tema
    else document.documentElement.dataset.tema = valor
    localStorage.setItem('tema', valor)
  }

  return (
    <section className="cartao pilha">
      <span className="rotulo-secao">Aparência</span>
      <div className="chips">
        {[['sistema', 'Sistema'], ['claro', 'Claro'], ['escuro', 'Escuro']].map(([valor, rotulo]) => (
          <button key={valor} type="button" className="chip" aria-pressed={tema === valor}
                  onClick={() => aplicar(valor)}>
            {rotulo}
          </button>
        ))}
      </div>
    </section>
  )
}
